<script setup>
import { computed, onMounted, ref, watch, onBeforeUnmount } from "vue";
import Swiper from "swiper";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import QChevronLeftIcon from "../../icons/QChevronLeftIcon.vue";
import QChevronRightIcon from "../../icons/QChevronRightIcon.vue";

const props = defineProps({
    value: {
        type: Array,
        default: () => [],
    },

    slidesPerView: {
        type: [Number, String],
        default: "auto",
    },

    slidesPerGroup: {
        type: Number,
        default: 1,
    },

    spaceBetween: {
        type: Number,
        default: 10,
    },

    direction: {
        type: String,
        default: "horizontal",
    },

    loop: {
        type: Boolean,
        default: false,
    },

    autoplay: {
        type: [Boolean, Object],
        default: false,
    },

    navigation: {
        type: Boolean,
        default: true,
    },

    pagination: {
        type: [Boolean, Object],
        default: false,
    },

    breakpoints: {
        type: Object,
        default: null,
    },

    verticalViewPortHeight: {
        type: String,
        default: "300px",
    },

    contentClass: {
        type: String,
        default: null,
    },

    containerClass: {
        type: String,
        default: null,
    },

    indicatorsContentClass: {
        type: String,
        default: null,
    },

    navigationOutside: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(["update:modelValue", "page"]);

const swiperContainer = ref(null);
const swiperInstance = ref(null);
const currentPage = ref(0);

const swiperOptions = computed(() => {
    const options = {
        slidesPerView: props.slidesPerView,
        slidesPerGroup: props.slidesPerGroup,
        spaceBetween: props.spaceBetween,
        direction: props.direction,
        loop: props.loop,
        breakpoints: props.breakpoints,
        breakpointsBase: "container",
        centerInsufficientSlides: true,
        containerModifierClass: "qmodii-slider",
    };

    if (props.navigation && !props.navigationOutside) {
        options.navigation = {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
        };
    }

    if (props.pagination) {
        options.pagination = {
            el: ".qmodii-slider-pagination",
            clickable: true,
            ...(typeof props.pagination === "object" ? props.pagination : {}),
        };
    }

    if (props.autoplay) {
        options.autoplay =
            typeof props.autoplay === "object"
                ? props.autoplay
                : { delay: 3000, disableOnInteraction: false };
    }

    return options;
});

const prevSlide = () => {
    if (swiperInstance.value) {
        swiperInstance.value.slidePrev();
    }
};

const nextSlide = () => {
    if (swiperInstance.value) {
        swiperInstance.value.slideNext();
    }
};

const isBeginning = ref(true);
const isEnd = ref(false);

const updateNavStates = () => {
    if (swiperInstance.value) {
        isBeginning.value = swiperInstance.value.isBeginning;
        isEnd.value = swiperInstance.value.isEnd;
    }
};

const destroySwiper = () => {
    if (swiperInstance.value) {
        swiperInstance.value.destroy(true, true);
        swiperInstance.value = null;
    }
};

const initSwiper = () => {
    if (swiperContainer.value) {
        destroySwiper();

        swiperInstance.value = new Swiper(swiperContainer.value, {
            modules: [Navigation, Pagination, Autoplay],
            ...swiperOptions.value,
        });

        updateNavStates();

        swiperInstance.value.on("slideChange", updateNavStates);
    }
};

watch(
    [
        () => props.slidesPerView,
        () => props.slidesPerGroup,
        () => props.spaceBetween,
        () => props.direction,
        () => props.loop,
        () => props.autoplay,
        () => props.navigation,
        () => props.pagination,
        () => props.breakpoints,
        () => props.navigationOutside,
        () => props.value.length,
    ],
    () => {
        if (swiperContainer.value) {
            setTimeout(() => {
                initSwiper();
            }, 0);
        }
    },
    { deep: true }
);

onBeforeUnmount(() => {
    destroySwiper();
});

onMounted(() => {
    initSwiper();
});
</script>

<template>
    <div class="qmodii-slider-container" :class="[containerClass]">
        <button
            v-if="props.navigation && props.navigationOutside"
            @click="prevSlide"
            class="qmodii-slider-nav qmodii-slider-nav-prev"
            :class="{ 'qmodii-slider-nav-disabled': isBeginning && !props.loop }"
            aria-label="Previous slide"
            :disabled="isBeginning && !props.loop"
        >
            <QChevronLeftIcon />
        </button>

        <div class="qmodii-slider-content" :class="[contentClass]">
            <div class="qmodii-slider swiper" ref="swiperContainer">
                <div class="qmodii-slider-wrapper swiper-wrapper">
                    <div
                        class="qmodii-slider-slide swiper-slide"
                        v-for="(item, index) in value"
                        :key="index"
                    >
                        <slot name="item" :data="item" :index="index"></slot>
                    </div>
                </div>

                <template v-if="props.navigation && !props.navigationOutside">
                    <div class="qmodii-slider-button-prev swiper-button-prev"></div>
                    <div class="qmodii-slider-button-next swiper-button-next"></div>
                </template>
            </div>

            <div v-if="props.pagination" class="qmodii-slider-pagination swiper-pagination"></div>
        </div>

        <button
            v-if="props.navigation && props.navigationOutside"
            @click="nextSlide"
            class="qmodii-slider-nav qmodii-slider-nav-next"
            :class="{ 'qmodii-slider-nav-disabled': isEnd && !props.loop }"
            aria-label="Next slide"
            :disabled="isEnd && !props.loop"
        >
            <QChevronRightIcon />
        </button>
    </div>
</template>

<style>
.qmodii-main {
    --qmodii-slider-nav-background: var(--qmodii-background);
    --qmodii-slider-nav-color: var(--qmodii-icon-color-primary);
    --qmodii-slider-nav-hover-background: var(--qmodii-primary-light);
    --qmodii-slider-nav-border: var(--qmodii-primary-border);
    --qmodii-slider-border-radius: 4px;
    --qmodii-slider-nav-size: 30px;
    --qmodii-slider-padding: var(--qmodii-spacing-lg);
    --qmodii-slider-nav-overlap: 15px;
    --qmodii-slider-nav-opacity: 0.9;
    --qmodii-slider-nav-z-index: 5;
    --qmodii-slider-nav-icon-size: 12px;
    --qmodii-slider-nav-offset: -4px;
    --qmodii-slider-pagination-spacing: 8px;
}
</style>

<style scoped>
.qmodii-slider-container {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
}

.qmodii-slider-content {
    width: 100%;
    height: 100%;
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
}

.qmodii-slider {
    width: 100%;
    flex: 1;
    padding: 0 var(--qmodii-slider-padding);
    box-sizing: border-box;
}

.qmodii-slider-pagination {
    position: relative !important;
    bottom: 0 !important;
    margin-top: var(--qmodii-slider-pagination-spacing) !important;
    text-align: center;
    width: 100%;
}

:deep(.swiper-pagination-bullet) {
    margin: 0 4px;
}

.qmodii-slider-nav {
    position: absolute;
    z-index: var(--qmodii-slider-nav-z-index);
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--qmodii-slider-nav-size);
    height: var(--qmodii-slider-nav-size);
    background: var(--qmodii-slider-nav-background);
    color: var(--qmodii-slider-nav-color);
    border: var(--qmodii-slider-nav-border);
    border-radius: 50%;
    cursor: pointer;
    opacity: var(--qmodii-slider-nav-opacity);
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 0;
    margin: 0;
}

.qmodii-slider-nav-prev {
    left: var(--qmodii-slider-nav-offset);
}

.qmodii-slider-nav-next {
    right: var(--qmodii-slider-nav-offset);
}

.qmodii-slider-nav:hover:not(.qmodii-slider-nav-disabled) {
    opacity: 1;
    background: var(--qmodii-slider-nav-hover-background);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

.qmodii-slider-nav-disabled {
    display: none;
}

.qmodii-slider-nav-icon {
    line-height: normal;
    font-weight: bold;
}
</style>
